import { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faGrid, faList } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AssignTemplatePagePermissionsModal from '@modals/AssignTemplatePagePermissionsModal/AssignTemplatePagePermissionsModal'
import {
  Box, Button, Columns, DashboardModule, Grid, LoadingModule, PageHeader,
  Tools,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import type { DefaultContext } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useIntegration from '@hooks/useIntegration'

import useTemplatePages from '@hooks/useTemplatePages'

import type { TemplatePageModel } from '@domain-group/realhub-backend-api-types'

import PageFilterModule from '@components/PageFilterModule'
import TemplatePageGridTile from '@components/TemplatePageGridTile/TemplatePageGridTile'

import EditTemplateModal from '@modals/EditTemplatePageModal/EditTemplatePageModal'

import CanvaTemplatePageBlankState from './CanvaTemplatePageBlankState'

import type { PageContextPayload } from '../List'
import ListView from './ListView'
import { AssignTemplatePagePermissionsModalPayload } from '@modals/AssignTemplatePagePermissionsModal/AssignTemplatePagePermissionsModal'
import BulkPermissionsModal from '@modals/BulkPermissionsModal/BulkPermissionsModal'
import Header from '../../../Header'

type BulkPermissionsModalPayload = { selectedTemplateIds: number[] }
const buildPageFilterFields = (isBrandUser: boolean) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        disabled: isBrandUser,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: ({ brand_id }: { brand_id?: number }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'templateGroups',
        label: 'Template Group',
      },
      key: 'template_group_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brandThemes',
        label: 'Brand Theme',
      },
      key: 'brand_theme_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: ({ brand_id }: { brand_id?: number }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
        label: 'Agency',
      },
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'services',
        label: 'Service',
      },
      key: 'service_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'tags',
        label: 'Tag',
      },
      key: 'tag_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: ({ service_id }: { service_id?: number }) => ({ service_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'serviceProducts',
        label: 'Product',
      },
      key: 'service_product_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Provider',
      },
      key: 'provider_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
        label: 'Publication',
      },
      key: 'publication_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
  assignTemplatePagePermissionsPayload: AssignTemplatePagePermissionsModalPayload | null,
  setAssignTemplatePagePermissionsPayload: (payload: AssignTemplatePagePermissionsModalPayload | null) => void
  showBulkPermissionsPayload: BulkPermissionsModalPayload,
  setShowBulkPermissionsPayload: (payload: BulkPermissionsModalPayload | null) => void
  selectedTemplateIds: number[],
  setSelectedTemplateIds: (ids: number[]) => void
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters, assignTemplatePagePermissionsPayload, setAssignTemplatePagePermissionsPayload, showBulkPermissionsPayload, setShowBulkPermissionsPayload, selectedTemplateIds, setSelectedTemplateIds } = props

  useLocation()

  const { currentUser, isBrandUser } = useCurrentUser()
  const scopedPageFilters = isBrandUser ? { ...pageFilters, brand_id: currentUser?.brand_id } : pageFilters
  const { integrated } = useIntegration()

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden?.toString() ?? 'false'

  const templatePagesPayload = useTemplatePages({
    filters: {
      ...scopedPageFilters,
      hidden: isHidden === 'true',
    },
    performHttpRequests: integrated,
    requestOptions: {
      include_template_page_dimensions: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
    },
  })

  const {
    callbacks: {
      loadMore,
      syncBrandTemplates,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading,
  } = templatePagesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const [selectedTemplatePage, setSelectedTemplatePage] = useState<null | TemplatePageModel>(null)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')

  return (
    <>
      <Helmet>
        <title>Canva Templates | Template Manager</title>
      </Helmet>

      <Header activeTabBarItemKey='linked_templates'/>

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            { !loading && !hasFilteredTemplatePages && (
              <CanvaTemplatePageBlankState
                integrated={integrated}
                hasFilteredTemplatePages={hasFilteredTemplatePages}
              />
            )}

            {integrated && !loading && hasFilteredTemplatePages && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Canva Templates">
                <Box flexDirection="column">
                  {viewMode === 'grid' && (
                    <Grid
                      // @ts-expect-error TS2322
                      gridGap="medium"
                      gridAutoRows="auto"
                      gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                      padding="large"
                    >
                      {filteredTemplatePages.map((templatePage: TemplatePageModel) => (
                        <TemplatePageGridTile
                          key={templatePage.id}
                          templatePage={templatePage}
                          setSelectedTemplatePage={setSelectedTemplatePage}
                        />
                      ))}
                    </Grid>
                  )}

                  {viewMode === 'list' && (
                    <ListView setSelectedTemplatePage={setSelectedTemplatePage} templatePages={filteredTemplatePages}/>
                  )}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          { integrated && (
          <Columns.Sidebar>
            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={viewMode === 'list' ? faGrid : faList} />}
                onClick={viewMode === 'list' ? () => setViewMode('grid') : () => setViewMode('list')}
                size="medium"
              >
                {viewMode === 'list' ? 'View as Grid' : 'View as List'}
              </Button>
            </Tools>

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={isBrandUser ? { brand_id: currentUser?.brand_id } : undefined}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(isBrandUser)}
            />
          </Columns.Sidebar>
          )}

           {integrated && selectedTemplatePage !== null && (
            <EditTemplateModal
              showModal
              modalKey="EditTemplateModal"
              templatePage={selectedTemplatePage}
              callbacks={{
                closeModal: () => { setSelectedTemplatePage(null) },
                editTemplatePage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
              }}
            />
          )}
          {assignTemplatePagePermissionsPayload !== null && (
          <AssignTemplatePagePermissionsModal
            callbacks={{
              bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
              closeModal: () => setAssignTemplatePagePermissionsPayload(null),
            }}
            showModal={true}
            payload={assignTemplatePagePermissionsPayload}
          />
        )}
          {showBulkPermissionsPayload !== null && (
          <BulkPermissionsModal
            modalKey="BulkPermissionsModal"
            selectedTemplateIds={selectedTemplateIds}
            callbacks={{
              closeModal: () => {
                setShowBulkPermissionsPayload(null)
                setSelectedTemplateIds([])
              },
              assignManually: ({ additionalTemplatePageIds, stepKey, templatePageId }) => {
                setShowBulkPermissionsPayload(null)
                setSelectedTemplateIds([])
                setAssignTemplatePagePermissionsPayload({
                  additionalTemplatePageIds,
                  stepKey,
                  templatePage: { id: templatePageId },
                })
              },
            }}
            showModal
          />
        )}
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
